import type {
    EnquirerType,
    EnquiryFormDestination,
    EnquiryProjectKind,
} from 'generated/graphql';
import {
    firstMatchingRuleIndex,
    regionSlug,
} from '~/modules/business-manager/forms/domain/routing-eval';
import {
    DEFAULT_RULE_MESSAGE,
    type DestinationValue,
    type EnquirerTypeValue,
    type ProjectKindValue,
} from '~/modules/business-manager/forms/types';
import type { PublicEnquiryForm } from '../types/public-enquiry-form';

export interface PredictedDestination {
    destination: EnquiryFormDestination;
    /** Set when the destination is an active meeting session. */
    calendarSessionId: number | null;
    /** Set when the destination is Message: what to show instead of the form. */
    message: string | null;
    /** Set when the destination is Branch: the network branch that receives it. */
    branchId: number | null;
}

/** Branch fields a public rule may carry; optional so older payloads still evaluate. */
type RuleBranchFields = {
    branchIds?: number[];
    destinationBranchId?: number | null;
};

/** Branch field a public form may carry for a Branch default destination. */
type FormBranchFields = { defaultDestinationBranchId?: number | null };

const isBranch = (destination: string): boolean => destination === 'Branch';

/**
 * Client-side mirror of the server's routing so the form can tell the visitor a
 * meeting will be offered, or show a rule's message instead of the form. The server
 * re-evaluates on submit and returns the real outcome as
 * `CreateEnquiryResult.RoutedDestination`; this is only a hint.
 */
export function predictEnquiryDestination(
    form: PublicEnquiryForm,
    choices: {
        typeId: number | null;
        enquirerType: EnquirerType | null;
        projectKind: EnquiryProjectKind | null;
        country: string | null;
        regionName: string | null;
        fieldAnswers?: Record<string, string>;
        /** The branch picked on a form that asks which branch. */
        branchId?: number | null;
    },
): PredictedDestination {
    const fallback: PredictedDestination = {
        destination: form.defaultDestination,
        calendarSessionId:
            form.defaultDestination === 'Calendar' &&
            form.calendarSession?.isActive
                ? form.calendarSession.id
                : null,
        message: null,
        branchId: isBranch(form.defaultDestination)
            ? ((form as FormBranchFields).defaultDestinationBranchId ?? null)
            : null,
    };
    if (!form.routingEnabled || form.routingRules.length === 0) {
        return fallback;
    }

    const rules = form.routingRules as ((typeof form.routingRules)[number] &
        RuleBranchFields)[];
    const index = firstMatchingRuleIndex(
        rules.map((rule) => ({
            enquiryFormTypeIds: rule.enquiryFormTypeIds,
            enquirerTypes: rule.enquirerTypes as EnquirerTypeValue[],
            projectKinds: rule.projectKinds as ProjectKindValue[],
            countries: rule.countries,
            regions: rule.regions,
            fieldConditions: rule.fieldConditions,
            branchIds: rule.branchIds ?? [],
            destination: rule.destination as DestinationValue,
        })),
        {
            typeId: choices.typeId,
            enquirerType: choices.enquirerType as EnquirerTypeValue | null,
            projectKind: choices.projectKind as ProjectKindValue | null,
            country: choices.country,
            regionSegment: regionSlug(choices.regionName),
            fieldAnswers: choices.fieldAnswers ?? {},
            branchId: choices.branchId ?? null,
        },
    );
    const rule = index >= 0 ? rules[index] : undefined;
    if (!rule) return fallback;
    return {
        destination: rule.destination,
        calendarSessionId:
            rule.destination === 'Calendar' ? rule.calendarSessionId : null,
        message:
            rule.destination === 'Message'
                ? rule.message || DEFAULT_RULE_MESSAGE
                : null,
        branchId: isBranch(rule.destination)
            ? (rule.destinationBranchId ?? null)
            : null,
    };
}
